import { FC, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '@amzn/awsui-components-core/header';
import ContentLayout from '@amzn/awsui-components-core/content-layout';
import Alert from '@amzn/awsui-components-core/alert';
import Spinner from '@amzn/awsui-components-core/spinner';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { useCampaignConfiguration } from 'src/hooks/useCampaignConfiguration';
import { useCampaignForm } from 'src/hooks/useCampaignForm';
import { useCreateCampaign } from 'src/hooks/useCreateCampaign';
import { useTranslate } from 'src/hooks/useTranslate';
import { buildRoute, CAMPAIGN_CONFIG, VALUE_MATCHING_BASE } from 'src/routes/valueMatching/routes';
import { CampaignType, OperationType } from 'src/@types/valueMatching/enums';
import { logError, trackClick, PAGE_IDS } from 'src/utils/monitoring';
import { CampaignForm } from 'src/components/organisms/CampaignForm';
import { FieldKey } from 'src/utils/valueMatching/form/formValidationUtils';
import ConflictTable from 'src/components/molecules/ConflictTable';

const CREATE_CAMPAIGN_BUNDLE = 'pages.createCampaign';
const VALIDATION_BUNDLE = 'shared.campaignValidation';
const MERCHANT_GROUP_TYPES = new Set([CampaignType.NO_COST_EMI, CampaignType.DEAL]);

const CreateCampaignPage: FC = () => {
    const { campaignType: slug = '' } = useParams<{ campaignType: string }>();
    const navigate = useNavigate();
    const { translate } = useTranslate(CREATE_CAMPAIGN_BUNDLE);
    const { translate: translateValidation } = useTranslate(VALIDATION_BUNDLE);
    const { config, loading, error: configError, campaignType } = useCampaignConfiguration(slug, OperationType.CREATE_CAMPAIGN);

    const showMerchantGroup = campaignType ? MERCHANT_GROUP_TYPES.has(campaignType) : false;
    const campaignTypeName = campaignType ? CAMPAIGN_CONFIG[campaignType].displayName : slug;

    const form = useCampaignForm({ config, campaignType, showMerchantGroup, mode: 'create' });

    const {
        submitCampaign,
        submitting,
        error: submitError,
        fieldErrors,
        markTouched,
        success,
        conflictingCampaignASINs,
    } = useCreateCampaign(form, config, campaignType);

    const listRoute = campaignType ? buildRoute.list(campaignType) : VALUE_MATCHING_BASE;

    useEffect(() => {
        if (success) {
            navigate(listRoute);
        }
    }, [success, navigate, listRoute]);

    const handleCancel = () => {
        trackClick('AbandonForm', { page: PAGE_IDS.CREATE_CAMPAIGN, ...(campaignType && { campaignType }) });
        navigate(listRoute);
    };

    const translatedFieldErrors = useMemo<Partial<Record<FieldKey, string>>>(() => {
        const result: Partial<Record<FieldKey, string>> = {};
        (Object.keys(fieldErrors) as Array<keyof typeof fieldErrors>).forEach((key) => {
            const msg = fieldErrors[key];
            if (msg) result[key] = translateValidation(msg.key, msg.params);
        });
        return result;
    }, [fieldErrors, translateValidation]);

    const pageHeader = (
        <Header variant="h1">
            {translate('create_title', { campaignType: campaignTypeName })}
        </Header>
    );

    if (loading) {
        return (
            <ContentLayout header={pageHeader}>
                <Box textAlign="center" padding={{ top: 'xxl' }}>
                    <Spinner size="large" />
                </Box>
            </ContentLayout>
        );
    }

    if (configError) {
        return (
            <ContentLayout header={pageHeader}>
                <Alert type="error" header={translate('error_loading_config')}>{configError}</Alert>
            </ContentLayout>
        );
    }

    return (
        <ContentLayout header={pageHeader}>
            <SpaceBetween size="l">
                {submitError && (
                    <Alert type="error" header={translate('error_header')}>
                        {submitError}
                    </Alert>
                )}

                {conflictingCampaignASINs.length > 0 && (
                    <Alert type="warning" header={translate('conflict_header')}>
                        <ConflictTable conflicts={conflictingCampaignASINs} />
                    </Alert>
                )}

                <CampaignForm
                    mode="create"
                    form={form}
                    config={config}
                    campaignType={campaignType}
                    showMerchantGroup={showMerchantGroup}
                    errors={translatedFieldErrors}
                    markTouched={markTouched}
                />

                <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={handleCancel}>
                        {translate('cancel_button')}
                    </Button>
                    <Button
                        variant="primary"
                        loading={submitting}
                        onClick={async () => {
                            trackClick('SubmitCampaign', {
                                page: PAGE_IDS.CREATE_CAMPAIGN,
                                ...(campaignType && { campaignType }),
                            });
                            try {
                                await submitCampaign();
                            } catch (e) {
                                // Error state is normally set inside the hook; log unexpected throws for debugging
                                logError('Campaign submission failed unexpectedly', {
                                    error: e instanceof Error ? e.message : String(e),
                                });
                            } finally {
                                window.scrollTo(0, 0);
                            }
                        }}
                    >
                        {translate('create_button')}
                    </Button>
                </SpaceBetween>
            </SpaceBetween>
        </ContentLayout>
    );
};

export default CreateCampaignPage;
